import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Contato } from '@eleg360/types';
import { contatosApi } from '@/lib/api/contatos';
import { datasApi, type DataComemorativa } from '@/lib/api/datas';
import { useAuthStore } from '@/lib/stores/authStore';
import { useBairros } from '@/lib/hooks/useBairros';
import { useEscala } from '@/lib/escala';
import { toast } from '@/lib/stores/toastStore';
import { Drawer } from '@/design-system/Drawer';
import { formatarDiaMes, formatarTelefone } from '@/lib/format';
import { podeEnviarWhatsApp, motivoBloqueio } from '@/lib/lgpd';
import { Info } from '@/design-system/Info';

const PP = 15;

const TIPOS_DATA = [
  { v: 'civica', l: 'cívica' },
  { v: 'setorial', l: 'setorial' },
  { v: 'tematica', l: 'temática' },
  { v: 'comunitaria', l: 'comunitária' },
  { v: 'direitos', l: 'direitos' },
];

/** MM-DD para DD/MM, para leitura. */
const exibirDiaMes = (diaMes: string) => {
  const [m, d] = diaMes.split('-');
  return d && m ? `${d}/${m}` : diaMes;
};

const ehHoje = (nascimento?: string) => {
  if (!nascimento) return false;
  const hoje = new Date();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(nascimento);
  if (!m) return false;
  return Number(m[2]) === hoje.getMonth() + 1 && Number(m[3]) === hoje.getDate();
};

export function DatasPage() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token) || '';
  const queryClient = useQueryClient();
  const { bairros } = useBairros();
  // Rotulo da unidade territorial segue a escala do mandato (bairro, municipio, estado...)
  const { plural } = useEscala();

  const [tab, setTab] = useState<0 | 1>(0);
  const [dias, setDias] = useState<number>(30);
  const [bairro, setBairro] = useState<string>('');
  const [base, setBase] = useState<string>('');
  const [page, setPage] = useState<number>(1);

  const [selecionado, setSelecionado] = useState<Contato | null>(null);
  const [msgCustom, setMsgCustom] = useState<string>('');

  const [modalData, setModalData] = useState<DataComemorativa | 'nova' | null>(null);

  const erroDe = (err: any, padrao: string) => {
    const msg = err?.response?.data?.message;
    return Array.isArray(msg) ? msg.join(' · ') : msg || padrao;
  };

  // ----- Aniversariantes (endpoint de contatos) -----
  const { data: aniversariantes = [], isLoading: carregandoNiver } = useQuery({
    queryKey: ['aniversariantes', dias, bairro],
    queryFn: () => contatosApi.listBirthdays(token, { dias, bairro: bairro || undefined }),
    enabled: !!token,
  });

  const lista = aniversariantes.filter((c) => !base || c.base_legal === base);
  const hoje = lista.filter((c) => ehHoje(c.nascimento));
  const semConsentimento = lista.filter((c) => c.base_legal !== 'optin');

  const pagesN = Math.max(1, Math.ceil(lista.length / PP));
  const paginaAtual = Math.min(page, pagesN);
  const rows = lista.slice((paginaAtual - 1) * PP, paginaAtual * PP);

  // ----- Datas comemorativas (endpoint próprio) -----
  const { data: datas = [], isLoading: carregandoDatas } = useQuery({
    queryKey: ['datas'],
    queryFn: () => datasApi.list(token),
    enabled: !!token,
  });

  const invalidarDatas = () => queryClient.invalidateQueries({ queryKey: ['datas'] });

  const criarData = useMutation({
    mutationFn: (dados: Partial<DataComemorativa>) => datasApi.create(dados, token),
    onSuccess: () => {
      invalidarDatas();
      toast('Data adicionada ao calendário.');
      setModalData(null);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao adicionar a data.')),
  });

  const atualizarData = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: Partial<DataComemorativa> }) =>
      datasApi.update(id, dados, token),
    onSuccess: () => {
      invalidarDatas();
      toast('Data atualizada.');
      setModalData(null);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao atualizar a data.')),
  });

  const excluirData = useMutation({
    mutationFn: (id: string) => datasApi.delete(id, token),
    onSuccess: () => {
      invalidarDatas();
      toast('Data removida do calendário.');
      setModalData(null);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao remover a data.')),
  });

  const abrirMensagem = (c: Contato) => {
    setSelecionado(c);
    const nomeCurto = c.nome.split(' ')[0];
    setMsgCustom(
      `Olá, ${nomeCurto}! Em nome do mandato da vereadora Ana Silva, desejamos um feliz aniversário! Muita saúde, paz e realizações. Conte sempre conosco!`,
    );
  };

  const trocarFiltro = (fn: () => void) => {
    fn();
    setPage(1);
  };

  return (
    <div className="page" id="p-datas">
      {/* Head */}
      <div className="head">
        <div>
          <h1>Aniversários e datas comemorativas</h1>
          <p>
            Todos os aniversariantes do período, inclusive quem não tem consentimento. Quem tem opt-in pode receber mensagem; os demais ficam para ligação.
          </p>
        </div>
        {tab === 1 && (
          <div className="act">
            <button className="btn pri" onClick={() => setModalData('nova')}>
              + Nova data
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button className={tab === 0 ? 'on' : ''} onClick={() => setTab(0)}>
          Aniversariantes
        </button>
        <button className={tab === 1 ? 'on' : ''} onClick={() => setTab(1)}>
          Datas comemorativas
        </button>
      </div>

      {/* ABA 0: ANIVERSARIANTES */}
      {tab === 0 && (
        <>
          <div className="grid g4">
            <div className="card kpi">
              <div className="l">Hoje</div>
              <div className="v" style={{ color: 'var(--good)' }}>{hoje.length}</div>
              <div className="d">
                {hoje.filter((c) => c.base_legal === 'optin').length} podem receber mensagem ·{' '}
                {hoje.filter((c) => c.base_legal !== 'optin').length} para ligação
              </div>
            </div>

            <div className="card kpi">
              <div className="l">No período</div>
              <div className="v">{lista.length}</div>
              <div className="d">próximos {dias} dias</div>
            </div>

            <div className="card kpi">
              <div className="l">Sem consentimento</div>
              <div className="v" style={{ color: semConsentimento.length ? 'var(--warn)' : 'var(--ink)' }}>
                {semConsentimento.length}
              </div>
              <div className="d">aparecem na lista, não recebem mensagem</div>
            </div>

            <div className="card kpi">
              <div className="l">Com opt-in</div>
              <div className="v">{lista.filter((c) => c.base_legal === 'optin').length}</div>
              <div className="d">elegíveis para felicitação por WhatsApp</div>
            </div>
          </div>

          {/* Filtros */}
          <div className="filters">
            <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Período:</span>
            {[7, 15, 30, 60, 90].map((d) => (
              <span
                key={d}
                className={`chip neutral chipbtn ${dias === d ? 'on' : ''}`}
                onClick={() => trocarFiltro(() => setDias(d))}
              >
                {d} dias
              </span>
            ))}
            <span style={{ flex: 1 }} />
            <select
              className="sel"
              value={bairro}
              onChange={(e) => trocarFiltro(() => setBairro(e.target.value))}
            >
              <option value="">Todos os {plural}</option>
              {bairros.map((b) => (
                <option key={b.id} value={b.n}>
                  {b.n}
                </option>
              ))}
            </select>
            <select
              className="sel"
              value={base}
              onChange={(e) => trocarFiltro(() => setBase(e.target.value))}
            >
              <option value="">Todas as bases legais</option>
              <option value="optin">Só com opt-in</option>
              <option value="sem_base">Sem base</option>
              <option value="optout">Opt-out</option>
            </select>
          </div>

          {/* Tabela */}
          <div className="card flat tw">
            <table>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Contato</th>
                  <th>Localidade</th>
                  <th>Perfil</th>
                  <th>Base legal</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const podeReceber = c.base_legal === 'optin';
                  const tel = (c.telefone_e164 || '').replace(/\D/g, '');

                  return (
                    <tr key={c.id} className="row">
                      <td className="num">
                        {formatarDiaMes(c.nascimento)}
                        {ehHoje(c.nascimento) ? ' 🎂' : ''}
                      </td>
                      <td>
                        <b>{c.nome}</b>
                        <div style={{ fontSize: 12, color: 'var(--ink3)' }} className="mono">
                          {formatarTelefone(c.telefone_e164)}
                        </div>
                      </td>
                      <td>
                        {c.bairro || '—'}
                        <br />
                        <span style={{ fontSize: 12, color: 'var(--ink3)' }}>
                          {c.municipio || 'Fortaleza'} · {c.uf || 'CE'}
                        </span>
                      </td>
                      <td>
                        {(c.tags?.length ? c.tags : ['Cidadão(ã)']).map((p) => (
                          <span key={p} className="chip neutral" style={{ marginRight: 4 }}>
                            {p}
                          </span>
                        ))}
                      </td>
                      <td>
                        <span className={`chip ${podeReceber ? 'good' : 'warn'}`}>
                          {podeReceber ? 'Opt-in' : c.base_legal === 'optout' ? 'Opt-out' : 'Sem base'}
                        </span>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {tel.length >= 10 ? (
                          <a className="btn sm" style={{ textDecoration: 'none' }} href={`tel:+${tel}`}>
                            📞 Ligar
                          </a>
                        ) : (
                          <button className="btn sm" style={{ opacity: 0.45 }} disabled>
                            📞 Ligar
                          </button>
                        )}{' '}
                        <button
                          className="btn sm"
                          disabled={!podeReceber || !tel}
                          title={
                            podeReceber
                              ? 'Compor felicitação'
                              : 'Contato sem consentimento registrado'
                          }
                          onClick={() => abrirMensagem(c)}
                        >
                          💬 Felicitar
                        </button>
                      </td>
                    </tr>
                  );
                })}

                {carregandoNiver && (
                  <tr>
                    <td colSpan={6} className="empty">
                      Carregando aniversariantes...
                    </td>
                  </tr>
                )}
                {!carregandoNiver && rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="empty">
                      Nenhum aniversariante nos próximos {dias} dias com os filtros aplicados.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="pager">
              <span style={{ marginRight: 'auto' }}>
                {lista.length} aniversariantes · página {paginaAtual} de {pagesN}
              </span>
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={paginaAtual === 1}>
                ‹
              </button>
              {Array.from({ length: pagesN }, (_, i) => i + 1)
                .filter((i) => Math.abs(i - paginaAtual) <= 3 || i === 1 || i === pagesN)
                .map((i) => (
                  <button key={i} className={paginaAtual === i ? 'on' : ''} onClick={() => setPage(i)}>
                    {i}
                  </button>
                ))}
              <button
                onClick={() => setPage((p) => Math.min(pagesN, p + 1))}
                disabled={paginaAtual === pagesN}
              >
                ›
              </button>
            </div>
          </div>
        </>
      )}

      {/* ABA 1: DATAS COMEMORATIVAS */}
      {tab === 1 && (
        <div className="card flat tw">
          <table>
            <thead>
              <tr>
                <th>Data</th>
                <th>Data comemorativa</th>
                <th>Tipo</th>
                <th>Ação planejada</th>
                <th>Público</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {datas.map((d) => (
                <tr key={d.id} className="row">
                  <td className="num">
                    <b>{exibirDiaMes(d.dia_mes)}</b>
                  </td>
                  <td>
                    <b>{d.titulo}</b>
                    {!d.ativa && (
                      <span className="chip neutral" style={{ marginLeft: 6 }}>
                        inativa
                      </span>
                    )}
                  </td>
                  <td>
                    <span className="chip neutral">
                      {TIPOS_DATA.find((t) => t.v === d.tipo)?.l || d.tipo}
                    </span>
                  </td>
                  <td>{d.acao_sugerida || '—'}</td>
                  <td>{d.tag ? <span className="chip acc">{d.tag}</span> : '—'}</td>
                  <td style={{ display: 'flex', gap: 6, whiteSpace: 'nowrap' }}>
                    <button className="btn sm" onClick={() => setModalData(d)}>
                      ✏️ Editar
                    </button>
                    <button className="btn sm" onClick={() => navigate('/campanhas')}>
                      Preparar campanha
                    </button>
                    <button className="btn sm" onClick={() => navigate('/agenda')}>
                      Agendar
                    </button>
                  </td>
                </tr>
              ))}

              {carregandoDatas && (
                <tr>
                  <td colSpan={6} className="empty">
                    Carregando calendário...
                  </td>
                </tr>
              )}
              {!carregandoDatas && datas.length === 0 && (
                <tr>
                  <td colSpan={6} className="empty">
                    Nenhuma data cadastrada. Use <b>+ Nova data</b> para montar o calendário do mandato.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Drawer: compor felicitação */}
      {selecionado && (
        <Drawer
          aberto
          titulo="Felicitação de aniversário"
          aoFechar={() => setSelecionado(null)}
          rodape={
            <>
              <button type="button" className="btn" onClick={() => setSelecionado(null)}>
                Cancelar
              </button>
              {podeEnviarWhatsApp(selecionado) && (
                <a
                  className="btn pri"
                  style={{ textDecoration: 'none' }}
                  href={`https://wa.me/${(selecionado.telefone_e164 || '').replace(/\D/g, '')}?text=${encodeURIComponent(msgCustom)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setSelecionado(null)}
                >
                  Abrir no WhatsApp
                </a>
              )}
            </>
          }
        >
          <div className="filters">
            <b>{selecionado.nome}</b>
            <span className="chip neutral">🎂 {formatarDiaMes(selecionado.nascimento)}</span>
            <span className={`chip ${selecionado.base_legal === 'optin' ? 'good' : 'warn'}`}>
              {selecionado.base_legal === 'optin' ? 'Opt-in' : 'Sem base'}
            </span>
          </div>

          {!podeEnviarWhatsApp(selecionado) && (
            <div className="note crit" style={{ marginTop: 12, fontSize: 12 }}>
              <b>Envio bloqueado.</b> {motivoBloqueio(selecionado)}
            </div>
          )}

          <div className="field" style={{ marginTop: 12 }}>
            <label>Mensagem</label>
            <textarea
              className="in"
              style={{ minHeight: 120 }}
              value={msgCustom}
              onChange={(e) => setMsgCustom(e.target.value)}
            />
          </div>

          <div className="wa" style={{ marginTop: 10 }}>
            <div className="bub">{msgCustom}</div>
          </div>

          {/*
            Fica visível: o botão parece disparar a mensagem, e o que ele faz é
            abrir a conversa. O restante do método foi para o ícone.
          */}
          <div className="note" style={{ marginTop: 12, fontSize: 12 }}>
            O envio abre a conversa no WhatsApp com o texto pronto.{' '}
            <Info titulo="Por que o envio não sai em lote daqui">
              O disparo automático em lote depende da fila de mensagens, que ainda não está ligada
              a esta tela.
            </Info>
          </div>
        </Drawer>
      )}

      {/* Modal: nova / editar data comemorativa */}
      {modalData && (
        <ModalData
          data={modalData === 'nova' ? null : modalData}
          salvando={criarData.isPending || atualizarData.isPending}
          excluindo={excluirData.isPending}
          onCancelar={() => setModalData(null)}
          onSalvar={(dados) => {
            if (modalData === 'nova') criarData.mutate(dados);
            else atualizarData.mutate({ id: modalData.id, dados });
          }}
          onExcluir={
            modalData === 'nova'
              ? undefined
              : () => {
                  if (window.confirm(`Remover "${modalData.titulo}" do calendário?`)) {
                    excluirData.mutate(modalData.id);
                  }
                }
          }
        />
      )}
    </div>
  );
}

function ModalData({
  data,
  salvando,
  excluindo,
  onCancelar,
  onSalvar,
  onExcluir,
}: {
  data: DataComemorativa | null;
  salvando: boolean;
  excluindo: boolean;
  onCancelar: () => void;
  onSalvar: (dados: Partial<DataComemorativa>) => void;
  onExcluir?: () => void;
}) {
  const [diaMes, setDiaMes] = useState(data?.dia_mes || '');
  const [titulo, setTitulo] = useState(data?.titulo || '');
  const [tipo, setTipo] = useState(data?.tipo || 'civica');
  const [acao, setAcao] = useState(data?.acao_sugerida || '');
  const [tag, setTag] = useState(data?.tag || '');
  const [ativa, setAtiva] = useState(data?.ativa ?? true);

  const submeter = (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(diaMes)) {
      toast('Informe a data no formato MM-DD (ex: 09-07).');
      return;
    }
    if (!titulo.trim()) {
      toast('O título da data é obrigatório.');
      return;
    }
    onSalvar({
      dia_mes: diaMes,
      titulo: titulo.trim(),
      tipo: tipo as DataComemorativa['tipo'],
      acao_sugerida: acao.trim() || undefined,
      tag: tag.trim() || undefined,
      ativa,
    });
  };

  return (
    <form onSubmit={submeter}>
      <Drawer
        aberto
        titulo={data ? 'Editar data' : 'Nova data comemorativa'}
        aoFechar={onCancelar}
        rodape={
          <>
            {onExcluir && (
              <button
                type="button"
                className="btn"
                style={{ color: 'var(--crit)', marginRight: 'auto' }}
                onClick={onExcluir}
                disabled={excluindo}
              >
                Excluir
              </button>
            )}
            <button type="button" className="btn" onClick={onCancelar}>
              Cancelar
            </button>
            <button type="submit" className="btn pri" disabled={salvando}>
              {salvando ? 'Salvando...' : data ? 'Salvar alterações' : 'Adicionar ao calendário'}
            </button>
          </>
        }
      >
        <div className="grid g2">
          <div className="field">
            <label>Data (MM-DD) *</label>
            <input
              className="in mono"
              placeholder="09-07"
              value={diaMes}
              onChange={(e) => setDiaMes(e.target.value)}
              required
            />
          </div>
          <div className="field">
            <label>Tipo</label>
            <select className="in" value={tipo} onChange={(e) => setTipo(e.target.value as any)}>
              {TIPOS_DATA.map((t) => (
                <option key={t.v} value={t.v}>
                  {t.l}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="field">
          <label>Título *</label>
          <input
            className="in"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            placeholder="Ex: Dia do Feirante"
            required
          />
        </div>

        <div className="field">
          <label>Ação planejada</label>
          <textarea
            className="in"
            style={{ minHeight: 70 }}
            value={acao}
            onChange={(e) => setAcao(e.target.value)}
            placeholder="O que o gabinete faz nesta data"
          />
        </div>

        <div className="grid g2">
          <div className="field">
            <label>Tag de público</label>
            <input
              className="in"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              placeholder="Ex: Comércio"
            />
          </div>
          <div className="field">
            <label>Situação</label>
            <select
              className="in"
              value={ativa ? '1' : '0'}
              onChange={(e) => setAtiva(e.target.value === '1')}
            >
              <option value="1">Ativa no calendário</option>
              <option value="0">Inativa</option>
            </select>
          </div>
        </div>

      </Drawer>
    </form>
  );
}
